import React, { useEffect, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoAlertCircleOutline } from "react-icons/io5";
import Main from "../../components/layout/Main";
import NameDialog from "../../components/layout/app/NameDialog";
import { useToggleTop } from "../../components/common/ToggleTop";
import { REMOTE_TYPES } from "../../constants/devices";
import { sendControl } from "../../services/deviceControl";
import { hapticTap } from "../../preferences/feedback";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import AcRemote from "./AcRemote";
import { KeyLayout, NumberPad } from "./RemoteKeys";
import { LAYOUTS, hasDimmedKeys } from "./layouts";
import { REMOTE_PATHS, blastPayload, blasterIdOf, changedNothing } from "./remoteUtils";
import "../../assets/styles/app-device.css";
import "../../assets/styles/app-remotes.css";

// While testing, the AC display remembers its temperature under this id.
const TEST_AC_ID = "irdb-test";

// The app's "Select … remote" (app/screens/HashhRemote/RemoteLayout.js): the
// brand's remote codes from the IRDB, one at a time with Prev and Next. Each
// is decoded into keys (irdb/decode), and a key press goes out from the real
// IR blaster, so the user finds the one that works their device. Save names it
// and adds it to the blaster (irdb/remote/add, source "irdb"), then goes back
// to Universal Remotes. Location state: { blaster, category, brand, steps }.
function TestRemote() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const blaster = state?.blaster;
  const category = state?.category;
  const brand = state?.brand;
  const [remotes, setRemotes] = useState(null);
  const [listStatus, setListStatus] = useState("loading");
  const [listAttempt, setListAttempt] = useState(0);
  const [index, setIndex] = useState(0);
  const [keys, setKeys] = useState(null);
  const [keyStatus, setKeyStatus] = useState("loading");
  const [keyAttempt, setKeyAttempt] = useState(0);
  const [numbersOpen, setNumbersOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!category || !brand) return undefined;
    let live = true;
    setListStatus("loading");
    UserService.getRemotes(category.id, brand.id)
      .then((response) => {
        if (!live) return;
        const list = Array.isArray(response) ? response : null;
        setRemotes(list ?? []);
        setIndex(0);
        setListStatus(list ? "ready" : "error");
      })
      .catch(() => live && setListStatus("error"));
    return () => {
      live = false;
    };
  }, [category, brand, listAttempt]);

  const remote = remotes?.[index];

  // The keys of the remote on screen, decoded again each time it's shown, as in the app.
  useEffect(() => {
    if (!remote) return undefined;
    let live = true;
    setKeys(null);
    setKeyStatus("loading");
    UserService.decodeRemote({ categoryId: category.id, remote_map: remote.remote_map, sub_cate: 1 })
      .then((response) => {
        if (!live) return;
        setKeys(response?.keys ?? {});
        setKeyStatus("ready");
      })
      .catch(() => live && setKeyStatus("error"));
    return () => {
      live = false;
    };
  }, [remote, category, keyAttempt]);

  if (!blaster || !category || !brand) return <Redirect to={REMOTE_PATHS.UNIVERSAL} />;

  const type = category.name_en?.toUpperCase();
  const rows = LAYOUTS[type];
  const title = `Select ${category.name_en} remote`;

  const codeFor = (name) =>
    keyStatus === "ready" && keys && Object.prototype.hasOwnProperty.call(keys, name) ? keys[name] || null : null;
  const isLearned = (name) => Boolean(codeFor(name));

  const press = async (name) => {
    const code = codeFor(name);
    if (!code) return;
    hapticTap();
    try {
      await sendControl(blastPayload(blasterIdOf(blaster), code));
    } catch (error) {
      toast("Couldn't send the command", "red");
    }
  };

  const show = (next) => {
    setNumbersOpen(false);
    setIndex(next);
  };

  // The app's RemoteNameModal: the remote as decoded, under the blaster's deviceId.
  const save = async (name) => {
    setSaving(true);
    try {
      const result = await UserService.addRemoteToProperty(User.getPropertyId(), {
        name,
        deviceId: blaster.deviceId,
        deviceType: category.name_en,
        source: "irdb",
        brand: brand.name_en,
        remote_map: remote.remote_map,
        keys,
      });
      if (changedNothing(result)) throw new Error("No blaster matched");
    } catch (error) {
      if (!alive.current) return;
      setSaving(false);
      toast("Failed to add remote!", "red");
      return;
    }
    if (!alive.current) return;
    toast("Added remote successfully!", "green");
    history.go(-state.steps);
  };

  let body;
  if (listStatus === "loading") {
    body = (
      <p className="hx-remote-status" role="status">
        Loading the {brand.name_en} remotes…
      </p>
    );
  } else if (listStatus === "error") {
    body = (
      <div className="hx-remote-status" role="alert">
        <IoAlertCircleOutline aria-hidden="true" />
        <span>Couldn't load the {brand.name_en} remotes.</span>
        <button type="button" className="hx-btn" onClick={() => setListAttempt((count) => count + 1)}>
          Try again
        </button>
      </div>
    );
  } else if (!remotes.length) {
    body = (
      <p className="hx-remote-status" role="status">
        There are no {brand.name_en} remotes in this category yet. Go back to choose another brand.
      </p>
    );
  } else {
    body = (
      <>
        <div className="hx-remote-picker" role="group" aria-label="Remotes to try">
          <button type="button" className="hx-btn" disabled={index === 0} onClick={() => show(index - 1)}>
            Prev
          </button>
          <span aria-live="polite">
            Remote {index + 1} of {remotes.length}
          </span>
          <button type="button" className="hx-btn" disabled={index >= remotes.length - 1} onClick={() => show(index + 1)}>
            Next
          </button>
        </div>
        <p className="hx-remote-note">
          Point {blaster.name ?? "the IR blaster"} at your {type} and press a key. If it responds, Save this remote;
          if not, try the next one.
        </p>
        {keyStatus === "loading" ? (
          <p className="hx-remote-status" role="status">
            Loading the remote's keys…
          </p>
        ) : null}
        {keyStatus === "error" ? (
          <div className="hx-remote-status" role="alert">
            <IoAlertCircleOutline aria-hidden="true" />
            <span>Couldn't load this remote's keys.</span>
            <button type="button" className="hx-btn" onClick={() => setKeyAttempt((count) => count + 1)}>
              Try again
            </button>
          </div>
        ) : null}
        {type === REMOTE_TYPES.AC ? (
          <AcRemote key={index} remoteId={TEST_AC_ID} isLearned={isLearned} onKey={press} />
        ) : rows ? (
          <KeyLayout
            rows={rows}
            isLearned={isLearned}
            onKey={press}
            onNumbers={() => setNumbersOpen(true)}
            numbersOpen={numbersOpen}
          />
        ) : (
          <div className="hx-empty">This type of remote can't be shown here yet.</div>
        )}
        {keyStatus === "ready" && hasDimmedKeys(type, isLearned) ? (
          <p className="hx-remote-note">Dimmed keys have no code in this remote.</p>
        ) : null}
      </>
    );
  }

  return (
    <Main
      header="stack"
      title={title}
      hideTabBar
      headerRight={
        <button type="button" className="hx-edit-update" disabled={keyStatus !== "ready" || !remote} onClick={() => setNaming(true)}>
          Save
        </button>
      }
    >
      <section className="hx-remote" aria-label={title} aria-busy={listStatus === "loading" || (Boolean(remote) && keyStatus === "loading")}>
        {body}
      </section>
      {rows ? (
        <NumberPad open={numbersOpen} onClose={() => setNumbersOpen(false)} isLearned={isLearned} onKey={press} />
      ) : null}
      <NameDialog
        open={naming}
        idPrefix="hx-remote-name"
        title="Enter Remote Name"
        label="Remote Name"
        submitLabel="Save"
        busy={saving}
        onClose={() => !saving && setNaming(false)}
        onSubmit={save}
      />
    </Main>
  );
}

export default TestRemote;
